import { Capacitor } from "@capacitor/core";
import { cx } from "class-variance-authority";
import { useCallback, useEffect, useRef, useState } from "react";

import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";
import { Icon } from "atoms/icon/Icon";
import LoadingSpin from "atoms/icon/icons/loading_spin_clean.svg";
import Send from "atoms/icon/icons/send_message.svg";

import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";

const MAX_HEIGHT = 120;

export type ChatMode = "free" | "streaming" | "warmingUp";

export const AdonisChatTextAreaContainer = ({
  handleSendRecommendation,
  keyboardIsOpen,
  keyboardHeight,
  placeholder,
  isWarmupComplete,
  isWarmingUp,
  isStreaming,
}: {
  handleSendRecommendation: (text?: string) => void;
  keyboardIsOpen: boolean;
  keyboardHeight: number;
  placeholder: string;
  isWarmupComplete: boolean;
  isWarmingUp: boolean;
  isStreaming: boolean;
}) => {
  const [recommendationInput, setRecommendationInput] = useState<string>("");
  const { selection } = useHaptics();
  const { session, persona } = useAdonisChat();
  const taRef = useRef<HTMLTextAreaElement>(null);

  const mode: ChatMode =
    !isWarmupComplete || isWarmingUp
      ? "warmingUp"
      : isStreaming
      ? "streaming"
      : "free";

  const storageKey = `adonis_chat_draft_${session.id}`;

  // Load draft from session storage on mount
  useEffect(() => {
    const draft = sessionStorage.getItem(storageKey);
    if (draft) {
      setRecommendationInput(draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id, storageKey]);

  // Auto-resize textarea - FIX: Always resize, even when empty
  useEffect(() => {
    // timeout to prevent layout thrashing with cleanup on unmount
    const timeout = setTimeout(() => {
      const ta = taRef.current;
      if (!ta) return;

      ta.style.height = "auto";
      const newH = Math.min(ta.scrollHeight, MAX_HEIGHT);

      ta.style.height = `${newH}px`;
      ta.style.overflowY = newH >= MAX_HEIGHT ? "auto" : "hidden";
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, [recommendationInput]);

  const onHandleSendRecommendation = useCallback(() => {
    if (mode !== "free") {
      return;
    }
    selection().catch(() => undefined);
    sessionStorage.removeItem(storageKey);
    handleSendRecommendation(recommendationInput);
    setRecommendationInput("");
  }, [
    handleSendRecommendation,
    mode,
    recommendationInput,
    selection,
    storageKey,
  ]);

  const isNative = Capacitor.isNativePlatform();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onHandleSendRecommendation();
      }
    },
    [onHandleSendRecommendation]
  );

  const handleFocus = () => {
    // On mobile web, the dynamic viewport units (dvh) will handle keyboard behavior automatically
    if (taRef.current && isNative) {
      setTimeout(() => {
        taRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  // Save draft on blur instead of every keystroke
  const handleBlur = () => {
    if (recommendationInput) {
      sessionStorage.setItem(storageKey, recommendationInput);
    } else {
      sessionStorage.removeItem(storageKey);
    }
  };

  const getPlaceholder = () => {
    if (mode === "warmingUp") {
      return `${persona.name} is getting ready to assist you...`;
    }
    return placeholder || "Type a message…";
  };

  const isDisabled = mode === "warmingUp";

  return (
    <div
      className={cx(
        "z-[2]",
        keyboardIsOpen ? "pb-4" : "pb-8",
        isNative && keyboardIsOpen && "relative"
      )}
      style={{
        ...(isNative &&
          keyboardIsOpen && {
            bottom: `${keyboardHeight + 10}px`,
          }),
      }}
    >
      <div className="px-4">
        <div className="flex rounded-2xl bg-white px-4 py-2">
          <textarea
            data-testid="adonis-chat-text-area"
            maxLength={250}
            ref={taRef}
            value={recommendationInput}
            onChange={(e) =>
              !isDisabled && setRecommendationInput(e.target.value)
            }
            onKeyDown={!isDisabled ? handleKeyDown : undefined}
            onFocus={!isDisabled ? handleFocus : undefined}
            onBlur={!isDisabled ? handleBlur : undefined}
            rows={1}
            placeholder={getPlaceholder()}
            disabled={isDisabled}
            className="sans max-h-[120px] flex-1 resize-none appearance-none items-center rounded-l-2xl border-none text-primary placeholder-gray-500 ring-1 ring-transparent focus:outline-none focus:ring-transparent focus:placeholder:!text-secondary disabled:bg-white disabled:text-gray-400"
          />
          <button
            onClick={onHandleSendRecommendation}
            disabled={!recommendationInput.trim() || mode !== "free"}
            className="flex items-end justify-end rounded-r-2xl bg-white"
          >
            <div
              className="flex h-[40px] w-[40px] items-center justify-center rounded-full"
              style={{
                backgroundColor:
                  !recommendationInput.trim() || mode !== "free"
                    ? "rgba(189, 162, 128, 0.5)"
                    : "#BDA280",
              }}
            >
              {mode === "streaming" || mode === "warmingUp" ? (
                <Icon
                  svg={LoadingSpin}
                  size="medium"
                  className="animate-spin text-white"
                />
              ) : (
                <Icon svg={Send} size="medium" className="text-white" />
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
